// src/viqai/ViqMarkdown.jsx
// ─── Rendu Markdown des réponses VIQ.AI ─────────────────────────────────────
// Les réponses affichaient les caractères bruts (### , ** , ---). Un modèle
// produit du Markdown : il faut le rendre, pas l'exposer.
//
// Écrit à la main plutôt qu'avec react-markdown, pour une raison précise : le
// bundle est déjà signalé comme trop lourd par CRA (568 kB gzip), et
// react-markdown + remark pèsent une centaine de kilo-octets pour un sous-
// ensemble de Markdown qu'un modèle n'utilise jamais entièrement. Ce renderer
// couvre exactement ce que VIQ.AI produit : titres, gras, italique, listes,
// listes numérotées, tableaux, citations, liens, code, séparateurs.
//
// Aucun HTML de la réponse n'est interprété : on ne construit que des éléments
// React. Un modèle qui renverrait <script> l'afficherait comme du texte.

import { Fragment } from "react";

// ── Rendu en ligne : gras, italique, code, liens ────────────────────────────
// Un seul passage, par priorité décroissante, pour que `**du `code`**` ne se
// coupe pas en morceaux incohérents.
const INLINE = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s<>()]+)/g;

function renderInline(text, keyPrefix = "i") {
  const parts = String(text ?? "").split(INLINE);
  return parts.map((p, i) => {
    if (!p) return null;
    const k = `${keyPrefix}-${i}`;
    if (p.startsWith("***") && p.endsWith("***")) return <strong key={k}><em>{p.slice(3, -3)}</em></strong>;
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={k}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) return <em key={k}>{p.slice(1, -1)}</em>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={k} className="viq-md-code">{p.slice(1, -1)}</code>;

    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const href = link[2].trim();
      // Seuls http(s) sont suivis : un `javascript:` dans une réponse de modèle
      // ne doit jamais devenir un lien cliquable.
      if (!/^https?:\/\//i.test(href)) return <Fragment key={k}>{link[1]}</Fragment>;
      return <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="viq-md-link">{link[1]}</a>;
    }
    if (/^https?:\/\//i.test(p)) {
      return <a key={k} href={p} target="_blank" rel="noopener noreferrer" className="viq-md-link">{p}</a>;
    }
    return <Fragment key={k}>{p}</Fragment>;
  });
}

// ── Tableaux ────────────────────────────────────────────────────────────────
const isTableSep = (l) => /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(l) && l.includes("-");
const splitRow = (l) => l.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map(c => c.trim());

export default function ViqMarkdown({ children, className = "" }) {
  const src = String(children ?? "");
  if (!src.trim()) return null;

  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let list = null;        // { ordered, items: [] }
  let quote = null;       // string[]
  let code = null;        // { lang, lines: [] }
  let key = 0;

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    out.push(
      <Tag key={`l${key++}`} className={list.ordered ? "viq-md-ol" : "viq-md-ul"}>
        {list.items.map((it, i) => <li key={i}>{renderInline(it, `l${key}-${i}`)}</li>)}
      </Tag>
    );
    list = null;
  };
  const flushQuote = () => {
    if (!quote) return;
    out.push(<blockquote key={`q${key++}`} className="viq-md-quote">{renderInline(quote.join(" "), `q${key}`)}</blockquote>);
    quote = null;
  };
  const flushCode = () => {
    if (!code) return;
    out.push(<pre key={`c${key++}`} className="viq-md-pre"><code>{code.lines.join("\n")}</code></pre>);
    code = null;
  };
  const flushAll = () => { flushList(); flushQuote(); };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();

    // Bloc de code : tout est pris littéralement jusqu'à la clôture.
    if (/^\s*```/.test(line)) {
      if (code) flushCode();
      else { flushAll(); code = { lines: [] }; }
      continue;
    }
    if (code) { code.lines.push(raw); continue; }

    if (!line.trim()) { flushAll(); continue; }

    // Séparateur
    if (/^\s*(---|___|\*\*\*)\s*$/.test(line)) {
      flushAll();
      out.push(<hr key={`h${key++}`} className="viq-md-hr" />);
      continue;
    }

    // Titres — plafonnés à h3 visuellement : une réponse de conversation ne
    // doit pas crier plus fort que le titre d'une carte.
    const h = line.match(/^\s*(#{1,6})\s+(.*)$/);
    if (h) {
      flushAll();
      const level = Math.min(h[1].length, 3);
      const Tag = `h${level + 1}`;
      out.push(<Tag key={`t${key++}`} className={`viq-md-h${level}`}>{renderInline(h[2], `t${key}`)}</Tag>);
      continue;
    }

    // Tableau : ligne d'en-tête suivie d'une ligne de séparation
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushAll();
      const head = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        rows.push(splitRow(lines[i])); i++;
      }
      i--;
      out.push(
        <div key={`tb${key++}`} className="viq-md-table-wrap">
          <table className="viq-md-table">
            <thead><tr>{head.map((c, k) => <th key={k}>{renderInline(c, `th${k}`)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{head.map((_, ci) => <td key={ci}>{renderInline(r[ci] || "", `td${ri}-${ci}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Citation
    const q = line.match(/^\s*>\s?(.*)$/);
    if (q) { flushList(); (quote = quote || []).push(q[1]); continue; }
    flushQuote();

    // Listes
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    if (ul) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(ul[1]);
      continue;
    }
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(ol[1]);
      continue;
    }
    flushList();

    // Paragraphe : les lignes consécutives sont fusionnées, comme en Markdown.
    const buf = [line.trim()];
    while (i + 1 < lines.length && lines[i + 1].trim()
           && !/^\s*(#{1,6}\s|[-*•]\s|\d+[.)]\s|>|```|---|___)/.test(lines[i + 1])
           && !(lines[i + 1].includes("|") && i + 2 < lines.length && isTableSep(lines[i + 2]))) {
      buf.push(lines[++i].trim());
    }
    out.push(<p key={`p${key++}`} className="viq-md-p">{renderInline(buf.join(" "), `p${key}`)}</p>);
  }

  flushCode();
  flushAll();

  return <div className={`viq-md ${className}`}>{out}</div>;
}
